export function IncomeValuationApproach() {
  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Method #1: Income Valuation Approach
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            The income approach is the most widely used and most important valuation method for apartment buildings. This approach values your property based on its ability to generate income, which is exactly how professional investors think about multifamily real estate.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">How the Income Approach Works</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              The income approach calculates your property's value by dividing the Net Operating Income (NOI) by the market capitalization rate (cap rate). This formula reflects the fundamental principle that apartment buildings are worth more when they generate higher income and less when operating expenses are high.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              The formula is simple but powerful: <strong>Property Value = Net Operating Income ÷ Cap Rate</strong>. For example, if your apartment building generates $120,000 in annual NOI and the market cap rate is 7%, your property would be valued at approximately $1,714,286.
            </p>
            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2 text-lg">Key Formula:</h4>
              <p className="text-gray-800 text-xl font-semibold mb-2">Value = NOI ÷ Cap Rate</p>
              <p className="text-gray-600">This is the single most important formula in commercial real estate valuation</p>
            </div>
          </div>
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/b7b6a55b5f57ce5dc979d802c4eaab99.jpg"
              alt="Income approach valuation calculation for apartment buildings"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-8 lg:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Calculating Net Operating Income (NOI)</h3>
          <p className="text-lg text-gray-700 mb-8 text-center max-w-4xl mx-auto leading-relaxed">
            Net Operating Income is the annual income your property generates after paying all operating expenses but before debt service (mortgage payments) and capital expenditures. Here's how to calculate it:
          </p>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
                  <span className="text-green-600 font-bold">1</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-gray-900 mb-2 text-lg">Gross Potential Rent</h4>
                  <p className="text-gray-600 mb-2">Total annual rent if 100% occupied at market rates</p>
                  <p className="text-gray-800 font-semibold">Example: 20 units × $1,200/month × 12 months = $288,000</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                  <span className="text-red-600 font-bold">-</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-gray-900 mb-2 text-lg">Vacancy & Credit Loss</h4>
                  <p className="text-gray-600 mb-2">Expected income loss from vacant units and non-paying tenants (typically 5-10%)</p>
                  <p className="text-gray-800 font-semibold">Example: $288,000 × 7% = -$20,160</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
                  <span className="text-green-600 font-bold">+</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-gray-900 mb-2 text-lg">Other Income</h4>
                  <p className="text-gray-600 mb-2">Laundry, parking, pet fees, storage, late fees, etc.</p>
                  <p className="text-gray-800 font-semibold">Example: $12,000 annually</p>
                </div>
              </div>
            </div>

            <div className="bg-teal-100 rounded-lg p-6 shadow-md border-2 border-teal-600">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-600 rounded-full flex-shrink-0">
                  <span className="text-white font-bold">=</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-gray-900 mb-2 text-lg">Effective Gross Income (EGI)</h4>
                  <p className="text-gray-800 font-semibold text-xl">$288,000 - $20,160 + $12,000 = $279,840</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                  <span className="text-red-600 font-bold">-</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-gray-900 mb-2 text-lg">Operating Expenses</h4>
                  <p className="text-gray-600 mb-2">Property taxes, insurance, utilities, maintenance, management, repairs</p>
                  <p className="text-gray-800 font-semibold">Example: $139,920 (50% of EGI is typical)</p>
                </div>
              </div>
            </div>

            <div className="bg-green-100 rounded-lg p-6 shadow-md border-2 border-green-600">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-green-600 rounded-full flex-shrink-0">
                  <span className="text-white font-bold">=</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-gray-900 mb-2 text-lg">Net Operating Income (NOI)</h4>
                  <p className="text-gray-800 font-semibold text-2xl">$139,920 annually</p>
                  <p className="text-gray-600 mt-2">This is the number used to calculate property value</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <img
              src="https://readdy.ai/api/search-image?query=capitalization-rate-cap-rate-real-estate-investment-analysis-market-comparison-chart-financial-metrics-apartment-building-valuation-investor-returns-yield-analysis-professional-presentation&width=800&height=600&seq=cap-rate-analysis-001&orientation=landscape"
              alt="Cap rate analysis for apartment building valuation"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Understanding Cap Rates</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              The capitalization rate (cap rate) represents the expected rate of return on a real estate investment. It's determined by market conditions and varies based on location, property quality, tenant stability, and perceived risk. Lower cap rates indicate higher property values and vice versa.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Cap rates typically range from 4% to 10% depending on the market. Class A properties in prime locations might trade at 4-6% cap rates, while Class C properties in secondary markets might trade at 8-10% cap rates. Your local market conditions determine the appropriate cap rate for your property.
            </p>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-teal-600">
                <h4 className="font-semibold text-gray-900 mb-1">Prime Location, Class A Property</h4>
                <p className="text-gray-600">Cap Rate: 4-6% | Higher value, lower returns, lower risk</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-yellow-500">
                <h4 className="font-semibold text-gray-900 mb-1">Good Location, Class B Property</h4>
                <p className="text-gray-600">Cap Rate: 6-8% | Moderate value, moderate returns, moderate risk</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-md border-l-4 border-orange-500">
                <h4 className="font-semibold text-gray-900 mb-1">Secondary Market, Class C Property</h4>
                <p className="text-gray-600">Cap Rate: 8-10% | Lower value, higher returns, higher risk</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg p-8 lg:p-12 text-white">
          <h3 className="text-3xl font-bold mb-6 text-center">Real-World Income Approach Example</h3>
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/10 rounded-lg p-6 mb-6">
              <h4 className="text-xl font-bold mb-4">Property Details:</h4>
              <ul className="space-y-2 text-lg">
                <li>• 24-unit apartment building in suburban market</li>
                <li>• Average rent: $1,100 per unit per month</li>
                <li>• Other income: $15,000 annually (laundry, parking, pet fees)</li>
                <li>• Operating expenses: $145,000 annually</li>
                <li>• Market cap rate: 7.5%</li>
              </ul>
            </div>
            <div className="bg-white/10 rounded-lg p-6">
              <h4 className="text-xl font-bold mb-4">Valuation Calculation:</h4>
              <div className="space-y-3 text-lg">
                <p>Gross Potential Rent: 24 units × $1,100 × 12 = <strong>$316,800</strong></p>
                <p>Vacancy Loss (7%): <strong>-$22,176</strong></p>
                <p>Other Income: <strong>+$15,000</strong></p>
                <p className="border-t border-white/30 pt-3">Effective Gross Income: <strong>$309,624</strong></p>
                <p>Operating Expenses: <strong>-$145,000</strong></p>
                <p className="border-t border-white/30 pt-3 text-xl">Net Operating Income: <strong>$164,624</strong></p>
                <p className="border-t-2 border-white pt-4 text-2xl font-bold">Property Value: $164,624 ÷ 0.075 = <strong>$2,194,987</strong></p>
                <p className="text-base opacity-90 mt-2">Rounded to: <strong>$2,195,000</strong></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}